/**
 * Materialized `rewards1d` / `rewards7d` from the indexer advance only when a new
 * distribution is indexed. For UI that should match "no yield in the window → 0",
 * treat stored values as stale after this many seconds without rollup activity.
 *
 * 8 × 86400 is slightly beyond the 7 UTC day buckets so idle pools zero out once
 * the true 7d window can no longer match the persisted non-decayed sum.
 */
export const REWARD_ROLLUP_STALE_SECONDS = 8 * 24 * 3600;

export type RewardRollupActivityPool = {
  isV2Grid?: boolean;
  lastRewardAt?: number | null;
  yield7dUpdatedAt?: number | null;
};

/** Latest known rollup activity (unix sec), or null if unknown. */
export function rewardRollupLastActivitySec(pool: RewardRollupActivityPool): number | null {
  if (pool.isV2Grid) {
    const t = pool.yield7dUpdatedAt;
    return t != null && t > 0 ? t : null;
  }
  const a = pool.lastRewardAt;
  const b = pool.yield7dUpdatedAt;
  if (a != null && a > 0) return a;
  if (b != null && b > 0) return b;
  return null;
}

/**
 * Return indexer `raw` reward string, or `"0"` if the rollup is stale for display.
 * When activity time is unknown, keep `raw` (conservative for migrations / missing fields).
 */
export function materializedRewardsRawOrZeroIfStale(
  raw: string | undefined,
  pool: RewardRollupActivityPool,
  nowSec: number = Math.floor(Date.now() / 1000),
): string {
  if (!raw || raw === '0') return raw ?? '0';
  const ts = rewardRollupLastActivitySec(pool);
  if (ts == null) return raw;
  if (nowSec - ts > REWARD_ROLLUP_STALE_SECONDS) return '0';
  return raw;
}
